import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  OPEN_EMS_BASE,
  aggregateOpenEmsTelemetry,
  applyOpenEmsSunnyBessBoost,
  buildOpenEmsGridlabUrl,
  fetchGridlabInsolation,
  fetchGridlabPowerFlow,
  fetchOpenEmsClusterPowerTelemetry,
  fetchOpenEmsDeyeInverters,
  fetchOpenEmsTelemetry,
  isLimitEmsSliderEnabled,
  parseTrackGridlabEntriesFromStationName,
  resolveEvPortInverterBindings,
  resolveOpenEmsInverterUrl,
  shouldApplyOpenEmsBatteryLimit,
} from '../utils/openEmsEvPortBattery';
import { fetchInsolationHourlyByDay } from '../utils/solarInsolationHourly';

const EMS_POLL_MS = 7000;

async function fetchDeyeBindingTelemetries(bindings) {
  const inverters = await fetchOpenEmsDeyeInverters();
  return Promise.all(
    (bindings || []).map(async binding => {
      const [baseTelemetry, clusterPower] = await Promise.all([
        fetchOpenEmsTelemetry(binding.deviceSn),
        fetchOpenEmsClusterPowerTelemetry(binding.deviceSn, inverters),
      ]);
      return {
        capacityKwh: binding.capacityKwh,
        telemetry: {
          ...baseTelemetry,
          pvPowerW: clusterPower.pvPowerW ?? baseTelemetry.pvPowerW,
          batteryPowerW: clusterPower.batteryPowerW ?? baseTelemetry.batteryPowerW,
          batteryDischargePowerW: clusterPower.batteryDischargePowerW ?? baseTelemetry.batteryDischargePowerW,
          gridPowerW: clusterPower.gridPowerW ?? baseTelemetry.gridPowerW,
          loadPowerW: clusterPower.loadPowerW ?? baseTelemetry.loadPowerW,
          socPercent:
            clusterPower.socPercent != null && Number.isFinite(Number(clusterPower.socPercent))
              ? Math.round(Number(clusterPower.socPercent))
              : baseTelemetry.socPercent,
        },
      };
    })
  );
}

async function fetchGridlabBindingTelemetries(entries) {
  return Promise.all(
    (entries || []).map(async entry => ({
      capacityKwh: entry.capacityKwh,
      telemetry: await fetchGridlabPowerFlow(entry.deviceId),
    }))
  );
}

export function useOpenEmsEvPortBatteryLimit(stationNumber, { stationName, latitude, longitude } = {}) {
  const [isEmsBound, setIsEmsBound] = useState(false);
  const [isEmsLoading, setIsEmsLoading] = useState(false);
  const [batteryCapacityKwh, setBatteryCapacityKwh] = useState(null);
  const [socPercent, setSocPercent] = useState(null);
  const [maxSelectableKwh, setMaxSelectableKwh] = useState(null);
  const [storedBatKwh, setStoredBatKwh] = useState(null);
  const [pvPowerW, setPvPowerW] = useState(null);
  const [batteryPowerW, setBatteryPowerW] = useState(null);
  const [insolationTodayPct, setInsolationTodayPct] = useState(null);
  const [insolationTomorrowPct, setInsolationTomorrowPct] = useState(null);
  const [openEmsInverterBindings, setOpenEmsInverterBindings] = useState([]);
  const [gridlabEntries, setGridlabEntries] = useState([]);
  const [openEmsLinkUrl, setOpenEmsLinkUrl] = useState(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(id);
  }, []);

  const limitEmsSliderEnabled = useMemo(() => isLimitEmsSliderEnabled(stationName), [stationName]);

  const boostedMaxSelectableKwh = useMemo(
    () =>
      applyOpenEmsSunnyBessBoost(maxSelectableKwh, {
        insolationTodayPct,
        now,
      }),
    [maxSelectableKwh, insolationTodayPct, now]
  );

  const shouldApplyBatteryLimit = useMemo(
    () =>
      limitEmsSliderEnabled &&
      openEmsInverterBindings.length > 0 &&
      boostedMaxSelectableKwh != null &&
      shouldApplyOpenEmsBatteryLimit(),
    [limitEmsSliderEnabled, openEmsInverterBindings.length, boostedMaxSelectableKwh]
  );

  const applyDisplayTelemetry = useCallback(telemetry => {
    if (telemetry.batteryCapacityKwh != null) setBatteryCapacityKwh(telemetry.batteryCapacityKwh);
    if (telemetry.socPercent != null) setSocPercent(telemetry.socPercent);
    if (telemetry.pvPowerW != null) setPvPowerW(Number(telemetry.pvPowerW));
    if (telemetry.batteryPowerW != null) setBatteryPowerW(Number(telemetry.batteryPowerW));
    if (telemetry.insolationTodayPct != null) setInsolationTodayPct(telemetry.insolationTodayPct);
    if (telemetry.insolationTomorrowPct != null) setInsolationTomorrowPct(telemetry.insolationTomorrowPct);
  }, []);

  useEffect(() => {
    if (!stationNumber || !stationName) {
      setOpenEmsInverterBindings([]);
      setGridlabEntries([]);
      setOpenEmsLinkUrl(null);
      setIsEmsBound(false);
      setIsEmsLoading(false);
      setBatteryCapacityKwh(null);
      setSocPercent(null);
      setMaxSelectableKwh(null);
      setStoredBatKwh(null);
      setPvPowerW(null);
      setBatteryPowerW(null);
      setInsolationTodayPct(null);
      setInsolationTomorrowPct(null);
      return undefined;
    }

    let cancelled = false;
    let pollId;
    let deyeBindings = [];
    let gridlab = parseTrackGridlabEntriesFromStationName(stationName);
    const stationNameSuggestsEms = /trackInverter\s*:/i.test(String(stationName || '')) || gridlab.length > 0;

    const resetTelemetry = () => {
      setIsEmsBound(false);
      setIsEmsLoading(false);
      setBatteryCapacityKwh(null);
      setSocPercent(null);
      setMaxSelectableKwh(null);
      setStoredBatKwh(null);
      setPvPowerW(null);
      setBatteryPowerW(null);
      setInsolationTodayPct(null);
      setInsolationTomorrowPct(null);
      setOpenEmsLinkUrl(null);
    };

    const refresh = async () => {
      const [deyeParts, glParts] = await Promise.all([
        deyeBindings.length > 0 ? fetchDeyeBindingTelemetries(deyeBindings) : Promise.resolve([]),
        gridlab.length > 0 ? fetchGridlabBindingTelemetries(gridlab) : Promise.resolve([]),
      ]);
      if (cancelled) return;

      // Slider limit stays Deye-only (plan: visual-only for GridLab).
      if (deyeParts.length > 0) {
        const deyeLimit = aggregateOpenEmsTelemetry(deyeParts);
        if (deyeLimit.maxSelectableKwh != null) setMaxSelectableKwh(deyeLimit.maxSelectableKwh);
        if (deyeLimit.storedBatKwh != null) setStoredBatKwh(deyeLimit.storedBatKwh);
      }

      const display = aggregateOpenEmsTelemetry([...deyeParts, ...glParts]);
      let insolationToday = null;
      let insolationTomorrow = null;
      for (const part of deyeParts) {
        if (part.telemetry?.insolationTodayPct != null) {
          insolationToday = part.telemetry.insolationTodayPct;
        }
        if (part.telemetry?.insolationTomorrowPct != null) {
          insolationTomorrow = part.telemetry.insolationTomorrowPct;
        }
      }
      if (
        (insolationToday == null || insolationTomorrow == null) &&
        Number.isFinite(Number(latitude)) &&
        Number.isFinite(Number(longitude))
      ) {
        if (gridlab.length > 0) {
          const insolation = await fetchGridlabInsolation(latitude, longitude);
          if (cancelled) return;
          if (insolationToday == null) insolationToday = insolation.insolationTodayPct;
          if (insolationTomorrow == null) insolationTomorrow = insolation.insolationTomorrowPct;
        }
        if (insolationToday == null || insolationTomorrow == null) {
          const forecast = await fetchInsolationHourlyByDay(latitude, longitude);
          if (cancelled) return;
          if (insolationToday == null) insolationToday = forecast.todayPct ?? null;
          if (insolationTomorrow == null) insolationTomorrow = forecast.tomorrowPct ?? null;
        }
      }

      applyDisplayTelemetry({
        ...display,
        insolationTodayPct: insolationToday,
        insolationTomorrowPct: insolationTomorrow,
      });

      if (deyeBindings.length > 0) {
        setOpenEmsLinkUrl(resolveOpenEmsInverterUrl(deyeBindings, display.batteryCapacityKwh));
      } else if (gridlab.length > 0) {
        setOpenEmsLinkUrl(buildOpenEmsGridlabUrl(gridlab[0].deviceId));
      }

      setIsEmsLoading(false);
    };

    const lookup = async () => {
      resetTelemetry();
      gridlab = parseTrackGridlabEntriesFromStationName(stationName);
      setGridlabEntries(gridlab);

      // Reserve solar/battery space immediately when station name implies Open EMS.
      if (stationNameSuggestsEms) {
        setIsEmsLoading(true);
      }

      try {
        if (/trackInverter\s*:/i.test(String(stationName || ''))) {
          const response = await fetch(`${OPEN_EMS_BASE}/api/deye/inverters`, { cache: 'no-store' });
          const data = await response.json().catch(() => ({}));
          if (response.ok && data.configured) {
            deyeBindings = resolveEvPortInverterBindings(data.items, stationName);
          }
        }
      } catch {
        deyeBindings = [];
      }

      if (cancelled) return;

      setOpenEmsInverterBindings(deyeBindings);
      if (deyeBindings.length === 0 && gridlab.length === 0) {
        setIsEmsLoading(false);
        return;
      }

      setIsEmsBound(true);
      setIsEmsLoading(true);
      await refresh();
      if (cancelled) return;

      pollId = window.setInterval(() => {
        void refresh();
      }, EMS_POLL_MS);
    };

    void lookup();

    return () => {
      cancelled = true;
      if (pollId) window.clearInterval(pollId);
    };
  }, [stationNumber, stationName, latitude, longitude, applyDisplayTelemetry]);

  return {
    isEmsBound,
    isEmsLoading,
    batteryCapacityKwh,
    socPercent,
    maxSelectableKwh: boostedMaxSelectableKwh,
    storedBatKwh,
    shouldApplyBatteryLimit,
    pvPowerW,
    batteryPowerW,
    insolationTodayPct,
    insolationTomorrowPct,
    openEmsInverterBindings,
    gridlabEntries,
    openEmsLinkUrl,
  };
}
